import {
  IQuoteRequestSchema,
  IQuoteRespSchema,
  OpportunityStatusEnum,
  QuoteStartDateSourceEnum,
  QuoteStatusEnum,
  QuoteTypeEnum,
} from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { ExperienceConfigurationKeys } from '@monetize/types/experience-config';
import { toDateShort } from '@monetize/utils/core';
import { UseFormReturn } from 'react-hook-form';
import { useGetContractById } from '../../../../../api/cpqService';
import { MBox, MButton, MText } from '../../../../../components/Monetize';
import {
  useExperienceConfiguration,
  useQuoteSettings,
} from '../../../../../hooks';
import { useAuth } from '../../../../../services/auth0';
import { useConfirmModal } from '../../../../../services/confirmModal';
import { logger } from '../../../../../services/logger';
import { canUserAcceptQuote, getQuoteStatus } from '../../../../../utils';
import {
  QuoteStateDataTypes,
  useQuoteContext,
} from '../../../Quote/quoteContext';
import { QuoteModalDataTypes } from '../../../Quote/QuoteReviewModal';
import { QuoteDraftSubmitButton } from './QuoteDraftSubmitButton';
import { QuoteSendButton } from './quoteSendButton/QuoteSendButton';
import { QuoteUnExpireButton } from './QuoteUnExpireButton';

interface QuoteActionButtonsProps {
  isActionLoading: boolean;
  quote: IQuoteRespSchema;
  isReadOnly: boolean;
  quoteStateData: QuoteStateDataTypes;
  quoteFormMethods: UseFormReturn<IQuoteRequestSchema>;
  reviewQuoteModalData: QuoteModalDataTypes;
  isEsignEnabled: boolean;
  setQuote: (quote: IQuoteRespSchema | null) => void;
  onAcceptQuote: () => void;
  onRecreateQuote: () => void;
  onUnExpireQuote: (expirationDate: string) => void;
  onRecreateAmendmentQuote: (data: {
    contractId: string;
    opportunityId: string;
    amendmentDate: Maybe<string>;
  }) => void;
  onRecreateRenewalQuote: (data: {
    contractId: string;
    opportunityId: string;
  }) => void;
  onSendQuote: () => void;
}

export const QuoteActionButtons = ({
  isActionLoading,
  quote,
  isReadOnly,
  quoteStateData,
  quoteFormMethods,
  reviewQuoteModalData,
  isEsignEnabled,
  setQuote,
  onAcceptQuote,
  onRecreateQuote,
  onUnExpireQuote,
  onRecreateAmendmentQuote,
  onRecreateRenewalQuote,
  onSendQuote,
}: QuoteActionButtonsProps) => {
  const { quoteAcceptanceAllowedRoles, quoteManualAcceptanceAllowedRoles } =
    useExperienceConfiguration(
      ExperienceConfigurationKeys.quoteAndContractSettings,
    );
  const { currentTenantUserHasRole } = useAuth();
  const {
    quoteData: { transitionQuoteStatus },
  } = useQuoteContext();

  const { quoteSettings } = useQuoteSettings();

  const pauseAtAccepted = !!quoteSettings?.pauseAtAccepted;

  const hasAbilityToAcceptOrProcessQuote =
    !quoteAcceptanceAllowedRoles?.length ||
    currentTenantUserHasRole(quoteAcceptanceAllowedRoles);

  const canAcceptQuote =
    !!quoteSettings &&
    canUserAcceptQuote({
      pauseAtAccepted,
      quoteAcceptanceAllowedRoles,
      quoteManualAcceptanceAllowedRoles,
      currentTenantUserHasRole,
    });
  const canProcessQuote = hasAbilityToAcceptOrProcessQuote;

  const { showConfirmModal, closeModal, setModalLoading } = useConfirmModal();

  const {
    isApproved,
    isCanceled,
    isExpired,
    isDraft,
    isSent,
    isAcceptedAdminEditable,
    isAccepted,
  } = getQuoteStatus(quote);

  const isOpportunityActive =
    quote.opportunity?.status === OpportunityStatusEnum.ACTIVE;

  const canRecreateAmendment =
    (isCanceled || isExpired) &&
    isOpportunityActive &&
    quote.type === QuoteTypeEnum.AMENDMENT &&
    !!quote.contractId;

  const canRecreateRenewal =
    (isCanceled || isExpired) &&
    isOpportunityActive &&
    quote.type === QuoteTypeEnum.RENEWAL &&
    !!quote.previousContractId;

  const showManualAcceptButton = isSent && pauseAtAccepted;

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const currentContractQuery = useGetContractById(quote.contractId!, {
    enabled: canRecreateAmendment,
  });

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const priorContractQuery = useGetContractById(quote.previousContractId!, {
    enabled: canRecreateRenewal,
  });

  const onAcceptChanges = () => {
    showConfirmModal({
      title: 'Are you sure you want to accept changes?',
      description:
        'Quote will be accepted with the changes made at your own risk.',
      onYes: async () => {
        setModalLoading(true);
        try {
          await transitionQuoteStatus(quote.id, {
            newState: QuoteStatusEnum.ACCEPTED,
          });
          closeModal();
        } catch (e) {
          logger.log(e);
        }
        setModalLoading(false);
      },
      yesButton: 'Accept',
      yesBtnProps: {
        variant: 'primary',
      },
      noBtnProps: {
        variant: 'cancel',
      },
    });
  };

  const onOpenProcessQuoteModal = () => {
    const shouldShowDelayedDate =
      quote.type === QuoteTypeEnum.NEW &&
      quote.startDateSource &&
      [
        QuoteStartDateSourceEnum.AT_TIME_OF_SIGNING,
        QuoteStartDateSourceEnum.MANUAL,
      ].includes(quote.startDateSource);

    showConfirmModal({
      title: 'Are you sure?',
      content: (
        <MBox>
          {shouldShowDelayedDate && (
            <MText mb="2">
              This quote will be processed and initiate billing on:{' '}
              <MText as="span" fontWeight="600">
                {quote.startDateSource ===
                QuoteStartDateSourceEnum.AT_TIME_OF_SIGNING
                  ? toDateShort(quote.acceptedAt)
                  : ''}
                {quote.startDateSource === QuoteStartDateSourceEnum.MANUAL
                  ? toDateShort(quote.contractStartDate)
                  : ''}
              </MText>
            </MText>
          )}

          <MText>
            Once this quote is processed, all information including Contract
            Start Date can no longer be edited.
          </MText>
        </MBox>
      ),
      onYes: async () => {
        try {
          setModalLoading(true);
          await transitionQuoteStatus(quote.id, {
            newState: QuoteStatusEnum.PROCESSED,
          });
        } catch (e) {
          console.error(e);
        }
        closeModal();
        setModalLoading(false);
      },
      yesButton: 'Process Quote',
      yesBtnProps: {
        variant: 'primary',
      },
      noBtnProps: {
        variant: 'cancel',
      },
    });
  };

  const onOpenUnExpireQuoteModal = () => {
    const shouldShowDelayedDate =
      quote.type === QuoteTypeEnum.NEW &&
      quote.startDateSource &&
      [
        QuoteStartDateSourceEnum.AT_TIME_OF_SIGNING,
        QuoteStartDateSourceEnum.MANUAL,
      ].includes(quote.startDateSource);

    showConfirmModal({
      title: 'Are you sure?',
      content: (
        <MBox>
          {shouldShowDelayedDate && (
            <MText mb="2">
              This quote will be processed and initiate billing on:{' '}
              <MText fontWeight="500">
                {quote.startDateSource ===
                QuoteStartDateSourceEnum.AT_TIME_OF_SIGNING
                  ? toDateShort(quote.acceptedAt)
                  : ''}
                {quote.startDateSource === QuoteStartDateSourceEnum.MANUAL
                  ? toDateShort(quote.contractStartDate)
                  : ''}
              </MText>
            </MText>
          )}

          <MText>
            Once this quote is processed, all information including Contract
            Start Date can no longer be edited.
          </MText>
        </MBox>
      ),
      onYes: async () => {
        try {
          setModalLoading(true);
          await transitionQuoteStatus(quote.id, {
            newState: QuoteStatusEnum.PROCESSED,
          });
        } catch (e) {
          console.error(e);
        }
        closeModal();
        setModalLoading(false);
      },
      yesButton: 'Process Quote',
      yesBtnProps: {
        variant: 'primary',
      },
      noBtnProps: {
        variant: 'cancel',
      },
    });
  };

  if (isDraft) {
    return (
      <QuoteDraftSubmitButton
        isReadOnly={isReadOnly}
        isActionLoading={isActionLoading}
        quote={quote}
        quoteStateData={quoteStateData}
        quoteFormMethods={quoteFormMethods}
        reviewQuoteModalData={reviewQuoteModalData}
      />
    );
  }

  if (isApproved) {
    return (
      <QuoteSendButton
        isActionLoading={isActionLoading}
        isEsignEnabled={isEsignEnabled}
        quote={quote}
        onSendQuote={onSendQuote}
        setQuote={setQuote}
      />
    );
  }

  if (canAcceptQuote && isSent && !quote.requiresEsign) {
    return (
      <MButton
        mr={1.5}
        variant="primary"
        data-testid="quote-accept-btn"
        isLoading={isActionLoading}
        isDisabled={isActionLoading}
        onClick={onAcceptQuote}
      >
        Accept Quote
      </MButton>
    );
  }

  if (isAcceptedAdminEditable) {
    return (
      <MButton
        mr={1.5}
        variant="primary"
        data-testid="quote-accept-btn"
        isLoading={isActionLoading}
        isDisabled={isActionLoading}
        onClick={onAcceptChanges}
      >
        Accept Changes
      </MButton>
    );
  }

  if (isAcceptedAdminEditable) {
    return (
      <MButton
        mr={1.5}
        variant="primary"
        data-testid="quote-accept-btn"
        isLoading={isActionLoading}
        isDisabled={isActionLoading}
        onClick={onAcceptChanges}
      >
        Manually Accept
      </MButton>
    );
  }

  if (canProcessQuote && isAccepted) {
    return (
      <MButton
        mr={1.5}
        variant="primary"
        data-testid="quote-accept-btn"
        isLoading={isActionLoading}
        isDisabled={isActionLoading}
        onClick={onOpenProcessQuoteModal}
      >
        Process Quote
      </MButton>
    );
  }

  if (isExpired && isOpportunityActive) {
    return (
      <QuoteUnExpireButton
        isActionLoading={isActionLoading}
        quote={quote}
        onUnExpireQuote={onUnExpireQuote}
      />
    );
  }

  if (isCanceled && quote.type === QuoteTypeEnum.NEW && isOpportunityActive) {
    return (
      <MButton
        mr={1.5}
        variant="secondary"
        data-testid="quote-recreate-btn"
        isLoading={isActionLoading}
        isDisabled={isActionLoading}
        onClick={onRecreateQuote}
      >
        Recreate Quote
      </MButton>
    );
  }

  /**
   * RE-CREATE AMENDMENT QUOTE (aka do another amendment)
   * If amendment quote is cancelled and the contract is not yet renewed, and the opp is still active:
   * allow "re-creating" the renewal quote. This will attach to the same opportunity.
   */
  if (
    canRecreateAmendment &&
    currentContractQuery.data &&
    !currentContractQuery.data.renewed &&
    quote.opportunity?.id
  ) {
    return (
      <MButton
        mr={1.5}
        variant="secondary"
        data-testid="quote-recreate-btn"
        isLoading={isActionLoading}
        isDisabled={isActionLoading}
        onClick={() => {
          if (!quote.opportunity?.id) {
            return quote.opportunity?.id;
          }
          onRecreateAmendmentQuote({
            contractId: currentContractQuery.data.id,
            opportunityId: quote.opportunity.id,
            amendmentDate: quote.contractAmendmentDate,
          });
        }}
      >
        Recreate Quote
      </MButton>
    );
  }

  /**
   * RE-CREATE RENEWAL QUOTE (aka do another renewal)
   * If renewal quote is cancelled and the contract is not yet renewed,
   * allow "re-creating" the renewal quote. This will attach to the same opportunity.
   */
  if (
    canRecreateRenewal &&
    priorContractQuery.data &&
    !priorContractQuery.data.renewed &&
    quote.opportunity?.id
  ) {
    return (
      <MButton
        mr={1.5}
        variant="secondary"
        data-testid="quote-recreate-btn"
        isLoading={isActionLoading}
        isDisabled={isActionLoading}
        onClick={() => {
          if (!quote.opportunity?.id) {
            return quote.opportunity?.id;
          }
          onRecreateRenewalQuote({
            contractId: priorContractQuery.data.id,
            opportunityId: quote.opportunity.id,
          });
        }}
      >
        Recreate Quote
      </MButton>
    );
  }

  return null;
};
